/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Form component for reporting bike maintenance issues. Handles
validation and submission of new maintenance requests and problem reports.
*/

import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";

type IssueType = Database["public"]["Enums"]["issue_type"];

interface ReportIssueFormProps {
  onSuccess?: () => void;
}

export function ReportIssueForm({ onSuccess }: ReportIssueFormProps) {
  const [selectedBike, setSelectedBike] = React.useState<string>("");
  const [issueType, setIssueType] = React.useState<IssueType>("other");
  const [notes, setNotes] = React.useState("");
  const [cost, setCost] = React.useState<string>(""); // cost as string for input binding
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: bikes, isLoading: bikesLoading } = useQuery({
    queryKey: ["bikes-for-maintenance"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bikes')
        .select(`
          id,
          serial_number,
          model:bike_models(name)
        `);

      if (error) {
        console.error('Error fetching bikes:', error);
        toast.error('Failed to load eBikes');
        throw error;
      }

      return data || [];
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBike) {
      toast.error('Please select an eBike');
      return;
    }

    let numericCost: number | null = null;
    if (cost.trim() !== "") {
      numericCost = Number(cost);
      if (isNaN(numericCost) || numericCost < 0) {
        toast.error('Please provide a valid maintenance fee (0 or greater)');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Insert the new maintenance issue
      const { data: insertedIssues, error } = await supabase
        .from('maintenance_issues')
        .insert({
          bike_id: selectedBike,
          issue_type: issueType,
          notes: notes.trim() || null,
          status: 'reported',
          cost: numericCost !== null ? numericCost : null
        })
        .select();

      if (error) throw error;

      // If a cost was provided, also insert into maintenance_issues_history
      if (insertedIssues && insertedIssues.length > 0) {
        const newIssue = insertedIssues[0];
        await supabase.from('maintenance_issues_history').insert({
          bike_id: selectedBike,
          issue_id: newIssue.id,
          status: 'reported',
          notes: notes.trim() || null,
          cost: numericCost !== null ? numericCost : null,
          changed_by: newIssue.reported_by || null, // may be null if not set
          change_timestamp: new Date().toISOString(),
        });
      }

      toast.success('Maintenance issue reported successfully');
      setSelectedBike("");
      setIssueType("other");
      setNotes("");
      setCost("");
      onSuccess?.();
    } catch (error) {
      console.error('Error reporting issue:', error);
      toast.error('Failed to report issue');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="bike">Select eBike</Label>
        <Select
          value={selectedBike}
          onValueChange={setSelectedBike}
        >
          <SelectTrigger className="bg-white">
            <SelectValue placeholder={bikesLoading ? "Loading eBikes..." : "Select an eBike"} />
          </SelectTrigger>
          <SelectContent>
            {bikes?.map((bike) => (
              <SelectItem key={bike.id} value={bike.id}>
                {bike.model?.name ? `${bike.model.name} - ${bike.serial_number.substring(0, 6)}` : `eBike ${bike.serial_number.substring(0, 6)}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="issueType">Issue Type</Label>
        <Select
          value={issueType}
          onValueChange={(value: IssueType) => setIssueType(value)}
        >
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="Select issue type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tires">Tires</SelectItem>
            <SelectItem value="brakes">Brakes</SelectItem>
            <SelectItem value="headset">Headset</SelectItem>
            <SelectItem value="wheels">Wheels</SelectItem>
            <SelectItem value="battery">Battery</SelectItem>
            <SelectItem value="missing">Missing</SelectItem>
            <SelectItem value="unreported">Unreported</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          placeholder="Describe the issue..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="min-h-[100px] bg-white"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost">Maintenance Fee (GBP)</Label>
        <Input
          id="cost"
          type="number"
          min="0"
          step="0.01"
          value={cost}
          onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))}
          placeholder="Enter maintenance fee (£)"
          className="bg-white"
        />
      </div>

      <Button 
        type="submit" 
        disabled={isSubmitting} 
        className="bg-blue-500 hover:bg-blue-600 text-white"
      >
        {isSubmitting ? "Submitting..." : "Report Issue"}
      </Button>
    </form>
  );
}
